// data-hide-if

// declare global {
//   interface Window {
//     initializeAllForms: () => void;
//   }
// }

type Condition = { name: string; value: string };

document.addEventListener('DOMContentLoaded', () => {
  initializeAllForms();
});

function initializeAllForms() {
  document.querySelectorAll<HTMLInputElement | HTMLElement>('[data-hide-if]').forEach((element) => {
    if (element.dataset.hideInitialized === 'true') {
      return;
    }
    element.dataset.hideInitialized = 'true';
    bindHide(element, element.dataset.hideIf as string, (formValue: string, conditionValue: string) => formValue !== conditionValue);
  });

  document.querySelectorAll<HTMLInputElement | HTMLElement>('[data-hide-unless]').forEach((element) => {
    if (element.dataset.hideInitialized === 'true') {
      return;
    }
    element.dataset.hideInitialized = 'true';
    bindHide(element, element.dataset.hideUnless as string, (formValue: string, conditionValue: string) => formValue === conditionValue);
  });
}

function bindHide(inputElement: HTMLInputElement | HTMLElement, conditionString: string, comparatorCallback: CallableFunction) {
  const containerElement =
    inputElement instanceof HTMLInputElement || inputElement instanceof HTMLSelectElement || inputElement instanceof HTMLTextAreaElement
      ? (inputElement.closest<HTMLElement>('.form-group') ?? inputElement.closest<HTMLElement>('.form-fieldset'))
      : inputElement;
  if (!containerElement) {
    console.warn('The element is not inside a .form-group or .form-fieldset element.', inputElement);
    return;
  }

  const form = inputElement.closest<HTMLFormElement>('form');
  if (!form) {
    console.warn('The element is not inside a form element.', inputElement);
    return;
  }

  const inputLabel = inputElement.closest<HTMLElement>('label');

  const isRequired =
    (inputElement instanceof HTMLInputElement || inputElement instanceof HTMLSelectElement || inputElement instanceof HTMLTextAreaElement
      ? inputElement.required
      : false) || inputLabel?.classList.contains('required') === true;

  // Parse the conditions from the {name}={value} format
  const conditions = conditionString
    .split(',')
    .map((conditionString) => {
      const [name, value] = conditionString.split('=');
      return { name, value };
    })
    .filter((condition) => condition.name !== null) as Condition[];

  form.addEventListener('change', () => checkConditions(inputElement, form, conditions, containerElement, comparatorCallback, isRequired));

  checkConditions(inputElement, form, conditions, containerElement, comparatorCallback, isRequired);
}
window.initializeAllForms = initializeAllForms;

function checkConditions(
  inputElement: HTMLInputElement | HTMLElement,
  form: HTMLFormElement,
  conditions: Condition[],
  containerElement: HTMLElement,
  comparatorCallback: CallableFunction,
  isRequired: boolean,
) {
  const conditionsMet = conditions.every((condition: any) => {
    const formElement = form[condition.name] as HTMLFormElement;

    if (formElement === undefined) {
      console.warn(`The form element ${condition.name} is not found.`);
      return false;
    }

    if (condition.value === 'checked') {
      return formElement.checked;
    }

    if (condition.value === '!checked') {
      return !formElement.checked;
    }

    return formElement.value === condition.value;
  });

  const visible = comparatorCallback(conditionsMet, true);
  containerElement.classList.toggle('hidden', !visible);

  if ((isRequired && inputElement instanceof HTMLInputElement) || inputElement instanceof HTMLSelectElement || inputElement instanceof HTMLTextAreaElement) {
    inputElement.required = visible;
  }
}

// validate number inputs with their data-scale and data-step attributes
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLInputElement>('input[data-scale]').forEach((element) => {
    if (element.dataset.scaleInitialized === 'true') {
      return;
    }
    element.dataset.scaleInitialized = 'true';
    bindNumberInput(element);
  });
});

function bindNumberInput(inputElement: HTMLInputElement) {
  const scale = parseInt(inputElement.dataset.scale ?? '1', 10);
  const precision = parseInt(inputElement.dataset.precision ?? '1', 10);

  // @ts-ignore
  inputElement.addEventListener('input', (e: InputEvent) => {
    if (inputElement.value === '') {
      return;
    }

    const value = inputElement.value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    let integer = parts[0];
    let decimal = parts[1] ?? '';

    if (decimal.length > scale) {
      decimal = decimal.slice(0, scale);
    }

    if (integer.length > precision - scale) {
      integer = integer.slice(0, precision - scale);
    }

    let decimalPoint = '';
    if (value.includes('.') || e.data === '.') {
      decimalPoint = `.`;
    }

    inputElement.value = `${integer}${decimalPoint}${decimal}`;
  });
}

// Whenever a submit button is clicked, turn its text content into a spinner, and disable it
// If someone presses back to come to the page though, restore the loading buttons into their normal form
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll<HTMLButtonElement>('button[type="submit"],input[type="submit"]').forEach((element) => {
    if (element.dataset.noAjax) {
      return;
    }
    if (element.dataset.submitInitialized === 'true') {
      return;
    }
    element.dataset.submitInitialized = 'true';
    bindSubmitButton(element);
  });
});

function bindSubmitButton(button: HTMLButtonElement | HTMLInputElement) {
  const form = button.closest('form');

  if (!form) {
    console.warn('The submit button is not inside a form element.', button);
    return;
  }

  form.addEventListener('submit', async (event) => {
    if (event.submitter !== button) {
      return;
    }

    if (form.checkValidity() !== true) {
      return;
    }

    event.preventDefault();

    disableAllSubmitButtons(button);
    disableSubmitButton(button, true);

    try {
      // Handle form submission
      const enable = await handleFormSubmission(form, button);
      if (enable) {
        enableAllSubmitButtons();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      enableAllSubmitButtons();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      enableAllSubmitButtons();
    }
  });
}

// TODO: lean this all up sometime...
async function handleFormSubmission(form: HTMLFormElement, button: HTMLButtonElement | HTMLInputElement): Promise<boolean> {
  const formData = new FormData(form);
  formData.append(button.name, button.value);
  const url = form.action;
  const method = form.method.toUpperCase();

  const isDownload = button.dataset.download !== undefined;

  if (method === 'GET') {
    if (!isDownload) {
      // Handle download GET requests
      const queryString = new URLSearchParams(formData as any).toString();
      const url = new URL(window.location.href);
      window.location.href = `${url.origin}${url.pathname}?${queryString}`;

      return false;
    }

    const queryString = new URLSearchParams(formData as any).toString();

    const url = new URL(window.location.href);

    const response = await fetch(`${url.origin}${url.pathname}?${queryString}`, {
      method,
      redirect: 'follow', // This ensures that fetch follows redirects
    });

    await handleResponse(response, true);

    return true;
  }

  // ? If we are not a download, we are a normal form submission
  if (!isDownload) {
    // just submit normally
    // Append the submit button value to the form data as a hidden value
    const submitInput = document.createElement('input');
    submitInput.type = 'hidden';
    submitInput.name = button.name;
    submitInput.value = button.value;
    form.appendChild(submitInput);
    HTMLFormElement.prototype.submit.call(form)
    return false;
  }

  const response = await fetch(url, {
    method,
    body: formData,
    redirect: 'follow', // This ensures that fetch follows redirects
  });

  await handleResponse(response, true);

  return true;
}

async function handleResponse(response: Response, forceDownload: boolean = false): Promise<void> {
  if (!response.ok && response.status !== 302) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  // Check if the response is a file download
  const contentDisposition = response.headers.get('Content-Disposition');
  const contentType = response.headers.get('Content-Type');
  if (contentDisposition && (contentDisposition.includes('attachment') || contentType === 'application/pdf' || forceDownload)) {
    // Handle file download
    const blobWithoutContentType = await response.blob();
    const blob = new Blob([blobWithoutContentType], { type: contentType || 'application/octet-stream' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = getFileNameFromContentDisposition(contentDisposition) || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Handle normal response or redirect
    const responseText = await response.text();

    if (response.redirected || response.url !== window.location.href) {
      // If it's a redirect or the URL has changed, update the page
      window.history.pushState({}, '', response.url);
      document.open();
      document.write(responseText);
      document.close();
    } else {
      // If it's not a redirect, update the page content
      document.body.innerHTML = responseText;
    }
  }
}

function getFileNameFromContentDisposition(contentDisposition: string): string | null {
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(contentDisposition);
  if (matches != null && matches[1]) {
    return matches[1].replace(/['"]/g, '');
  }
  return null;
}

function disableSubmitButton(button: HTMLButtonElement | HTMLInputElement, loader: boolean = false) {
  button.dataset.softDisabled = 'true';
  button.classList.add('disabled');

  if (loader) {
    button.dataset.loading = 'true';
    button.dataset.innerHTML = button.innerHTML ?? '';
    button.innerHTML =
      '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ' + (button.dataset.loadingText ?? button.innerHTML);
  }
}

function enableSubmitButton(button: HTMLButtonElement | HTMLInputElement) {
  if (button.dataset.softDisabled !== 'true') {
    return;
  }
  delete button.dataset.softDisabled;
  button.classList.remove('disabled');

  if (button.dataset.loading === 'true') {
    button.innerHTML = button.dataset.innerHTML ?? '';

    delete button.dataset.loading;
    delete button.dataset.innerHTML;
  }
}

function disableAllSubmitButtons(except?: HTMLElement | null) {
  document.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button[type="submit"],input[type="submit"]').forEach((button) => {
    if (button !== except) {
      disableSubmitButton(button);
    }
  });
}

function enableAllSubmitButtons() {
  document.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button[type="submit"],input[type="submit"]').forEach(enableSubmitButton);
}

window.enableAllSubmitButtons = enableAllSubmitButtons;

// when a btn-ajax-submit is clicked, submit the form via AJAX, and disable other buttons in that TR
document.addEventListener('DOMContentLoaded', () => {
  bindAjaxSubmitButtons();
});

// window on searchComplete
window.addEventListener('searchComplete', () => {
  bindAjaxSubmitButtons();
});

function bindAjaxSubmitButtons() {
  document.querySelectorAll<HTMLButtonElement>('.btn-ajax-submit').forEach((button) => {
    if (button.dataset.ajaxInitialized === 'true') {
      return;
    }
    button.dataset.ajaxInitialized = 'true';
    bindAjaxSubmit(button);
  });
}

function bindAjaxSubmit(button: HTMLButtonElement) {
  const form = button.closest('form');

  if (!form) {
    console.warn('The submit button is not inside a form element.', button);
    return;
  }

  button.addEventListener('click', async () => {
    const tr = button.closest('tr');
    if (!tr) {
      console.warn('The submit button is not inside a table row element.', button);
      return;
    }

    const otherButtons = tr.querySelectorAll<HTMLButtonElement>('.btn-ajax-submit[data-action=' + button.dataset.action + ']');
    otherButtons.forEach((otherButton) => {
      otherButton.disabled = true;
      otherButton.classList.add('disabled');
    });

    try {
      button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> ' + button.innerHTML;
      const formData = new FormData(form);
      formData.append(button.name, button.value);
      const url = form.action;
      const method = form.method.toUpperCase();
      const response = await fetch(url, {
        method,
        body: formData,
        // redirect: 'follow', // This ensures that fetch follows redirects
      });
      button.innerHTML = '<i class="fas fa-check"></i>';
    } catch (error) {
      console.error('Form submission error:', error);
    }
  });
}
